import { ParseStepResult, parse } from 'papaparse';
import IParseCsvReturn from './interfaces/IParseCsvReturn';
import ILoadCreation from '../../interfaces/ILoadCreation';
import { getLoadManagerAxiosInstance } from '../../../../../common/services/axios-config';
import IParseCsvRaw from './interfaces/IParseCsvRaw';
import IPostLoadsResult from './interfaces/IPostLoadsResult';
import {
    getDateFromStringDDMMYYYY,
    parseStringToNumber,
} from '../../utils/loads-utils';

const serviceCode = 'SPOT';
const defaultCountryPoc = 'Brazil';
const csvMimeType = 'text/csv';

const AddLoadRate = (load: ILoadCreation, data: IParseCsvRaw): void => {
    load.details.rates.push({
        code: `${data.CarrierCode}-DIR`,
        carrierCode: data.CarrierCode,
        serviceCode: {
            id: '',
            code: serviceCode,
        },
        vehicle: {
            id: data.EquipmentTypeCode,
            name: data.EquipmentTypeCode,
            description: data.EquipmentTypeName,
        },
        chargeAmount: parseStringToNumber(data.ChargeAmount),
        carrierScoreCard: 0,
        carrierId: '',
    });
};

const AddLoad = (parseResult: IParseCsvReturn, data: IParseCsvRaw): void => {
    parseResult.data.push({
        country: defaultCountryPoc,
        loadId: data.SystemLoadId,
        details: {
            channel: data.Channel,
            dates: {
                planningDate: new Date(0),
                collectionDate: getDateFromStringDDMMYYYY(data.CollectionDate),
                deliveryDate: new Date(0),
            },
            route: {
                distance: 0.1,
                source: {
                    code: data.OriginCode,
                    description: data.OriginDescription,
                    address: data.OriginAddress,
                    city: data.OriginCity,
                    federativeUnit: data.OriginFederativeUnit,
                },
                destination: {
                    code: data.DestinationCode,
                    description: data.DestinationDescription,
                    address: data.DestinationAddress,
                    city: data.DestinationCity,
                    federativeUnit: data.DestinationFederativeUnit,
                },
            },
            shipment: {
                material: {
                    id: data.CommodityCode,
                    name: data.CommodityName,
                },
                packing: {
                    id: '',
                    name: '',
                },
                currency: data.ShipmentCurrency,
                weight: parseStringToNumber(data.ShipmentWeight),
                weightUnitMeasure: data.ShipmentWeightUnit,
                volume: 0.1,
                skids: parseStringToNumber(data.ShipmentSkids) || 0,
                freightOrder: '',
            },
            vehicle: {
                id: data.EquipmentTypeCode,
                name: data.EquipmentTypeCode,
                description: data.EquipmentTypeName,
            },
            billing: {
                sapOrderNumber: '',
                shipmentNumber: data.ShipmentNumber,
                chargeAmount: parseStringToNumber(data.ChargeAmount),
                sequenceNumber: '0',
                serviceCode: {
                    id: '',
                    code: serviceCode,
                },
            },
            rates: [
                {
                    code: `${data.CarrierCode}-DIR`,
                    carrierCode: data.CarrierCode,
                    serviceCode: {
                        id: '',
                        code: serviceCode,
                    },
                    vehicle: {
                        id: data.EquipmentTypeCode,
                        name: data.EquipmentTypeCode,
                        description: data.EquipmentTypeName,
                    },
                    chargeAmount: parseStringToNumber(data.ChargeAmount),
                    carrierScoreCard: 0,
                    carrierId: '',
                },
            ],
            isPrioritized: false,
            isScoreCardEnabled: false,
        },
        createdAt: new Date(Date.now()),
    });
};

const AddOrUpdateData = (
    data: IParseCsvRaw,
    parseResult: IParseCsvReturn,
): void => {
    const load: ILoadCreation | undefined = parseResult.data.find(
        element => element.loadId === data.SystemLoadId,
    );

    if (load) {
        AddLoadRate(load, data);
    } else {
        AddLoad(parseResult, data);
    }
};

const handleStep = (
    row: ParseStepResult<IParseCsvRaw>,
    parseResult: IParseCsvReturn,
): void => {
    if (row.errors.length) {
        parseResult.errors.push(row.errors);
    } else {
        AddOrUpdateData(row.data, parseResult);
    }
};

const parseCsv = async (file: File | undefined): Promise<IParseCsvReturn> => {
    if (file === undefined) {
        return {
            data: [],
            errors: [],
        } as IParseCsvReturn;
    }

    if (file?.type !== csvMimeType) {
        return {
            data: [],
            errors: [],
        } as IParseCsvReturn;
    }

    const parseResult = {
        data: [],
        errors: [],
    } as IParseCsvReturn;

    return new Promise(resolve => {
        parse<IParseCsvRaw>(file, {
            header: true,
            skipEmptyLines: 'greedy',
            step(actualStep) {
                handleStep(actualStep, parseResult);
            },
            complete() {
                resolve(parseResult);
            },
        });
    });
};

const postToBackend = async (
    load: ILoadCreation,
    loadsResult: IPostLoadsResult,
    t: (key: string) => string,
): Promise<IPostLoadsResult> => {
    const Api = await getLoadManagerAxiosInstance();

    try {
        await Api.post('/v1/loads', {
            Country: load.country,
            LoadId: load.loadId,
            Details: load.details,
            CreatedAt: load.createdAt,
        });

        loadsResult.totalProcessed += 1;

        return loadsResult;
    } catch {
        loadsResult.totalErrors += 1;
        loadsResult.errors.push({
            loadId: load?.loadId ?? 'LoadId',
            error: t('ErroPostLoad'),
        });

        return loadsResult;
    }
};

const postLoad = async (
    loads: ILoadCreation[],
    t: (key: string) => string,
): Promise<IPostLoadsResult> => {
    const loadsResult = {
        totalProcessed: 0,
        totalErrors: 0,
        errors: [],
    } as IPostLoadsResult;

    const posts = loads.map(async load => postToBackend(load, loadsResult, t));

    await Promise.all(posts);

    return loadsResult;
};

const CsvImportService = { parseCsv, postLoad };

export default CsvImportService;
